import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { getCurrentUser } from '../auth/LoginButton';

interface CreateSurveyProps {
  onCancel: () => void;
  onSurveyCreated: () => void;
}

const CreateSurvey: React.FC<CreateSurveyProps> = ({ onCancel, onSurveyCreated }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    unique_url_slug: '',
    is_anonymous: true,
    allow_multiple_submissions: false,
    start_time: '',
    end_time: '',
    send_confirmation_email: false,
    confirmation_email_subject: '',
    confirmation_email_template: '',
    webhook_enabled: false,
    webhook_url: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [componentLoaded, setComponentLoaded] = useState(false);

  // Debug user role and component loading
  useEffect(() => {
    const user = getCurrentUser();
    console.log('📝 [CreateSurvey] Component mounted');
    console.log('📝 [CreateSurvey] Current user:', user);
    console.log('📝 [CreateSurvey] User role:', user?.role);
    setComponentLoaded(true);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Auto-generate slug from title
    if (name === 'title' && !formData.unique_url_slug) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setFormData(prev => ({ ...prev, unique_url_slug: slug }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare data - remove empty time fields
      const payload: any = {
        title: formData.title,
        description: formData.description || undefined,
        unique_url_slug: formData.unique_url_slug || undefined,
        is_anonymous: formData.is_anonymous,
        allow_multiple_submissions: formData.allow_multiple_submissions,
        send_confirmation_email: formData.send_confirmation_email,
        confirmation_email_subject: formData.confirmation_email_subject || undefined,
        confirmation_email_template: formData.confirmation_email_template || undefined,
        webhook_enabled: formData.webhook_enabled,
        webhook_url: formData.webhook_url || undefined
      };

      if (formData.start_time) {
        payload.start_time = new Date(formData.start_time).toISOString();
      }
      if (formData.end_time) {
        payload.end_time = new Date(formData.end_time).toISOString();
      }

      await axios.post('/surveys', payload);
      console.log('✅ [CreateSurvey] Survey created successfully');
      onSurveyCreated();
    } catch (err: any) {
      console.error('❌ [CreateSurvey] Error creating survey:', err);
      console.error('❌ [CreateSurvey] Error response:', err.response?.data);
      setError(err.response?.data?.message || 'Failed to create survey');
    } finally {
      setLoading(false);
    }
  };

  // Loading state indicator
  if (!componentLoaded) {
    return (
      <div style={{ 
        maxWidth: '800px', 
        margin: '2rem auto', 
        padding: '2rem',
        textAlign: 'center',
        color: 'var(--text-primary)'
      }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
        <div>Loading survey creator...</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem' }}>
        <button
          onClick={onCancel}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--accent)',
            cursor: 'pointer',
            fontSize: '1rem',
            padding: '0.5rem 0',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          ← Back to Surveys
        </button>
      </div>

      <div style={{
        background: 'var(--card-bg)',
        borderRadius: '12px',
        padding: '2rem',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ margin: '0 0 1.5rem', fontSize: '1.8rem' }}>Create New Survey</h2>

        {error && (
          <div style={{
            background: 'var(--error-bg)',
            border: '1px solid var(--error)',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            color: 'var(--error)'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              Survey Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="Enter survey title"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe your survey (optional)"
              rows={3}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '1rem',
                boxSizing: 'border-box',
                fontFamily: 'inherit'
              }}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              URL Slug
            </label>
            <input
              type="text"
              name="unique_url_slug"
              value={formData.unique_url_slug}
              onChange={handleChange}
              placeholder="custom-url-slug"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
              Used for friendly URLs. Auto-generated from title if left empty.
            </small>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '1rem', 
            marginBottom: '1.5rem' 
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                Start Time (optional)
              </label>
              <input
                type="datetime-local"
                name="start_time"
                value={formData.start_time}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                End Time (optional)
              </label>
              <input
                type="datetime-local"
                name="end_time"
                value={formData.end_time}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              cursor: 'pointer',
              marginBottom: '0.75rem'
            }}>
              <input
                type="checkbox"
                name="is_anonymous"
                checked={formData.is_anonymous}
                onChange={handleChange}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>Anonymous Responses</span>
            </label>
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', display: 'block', marginLeft: '26px' }}>
              Don't collect user identity with responses
            </small>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              cursor: 'pointer',
              marginBottom: '0.75rem'
            }}>
              <input
                type="checkbox"
                name="allow_multiple_submissions"
                checked={formData.allow_multiple_submissions}
                onChange={handleChange}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>Allow Multiple Submissions</span>
            </label>
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', display: 'block', marginLeft: '26px' }}>
              Users can submit the survey multiple times
            </small>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              cursor: 'pointer',
              marginBottom: '0.75rem'
            }}>
              <input
                type="checkbox"
                name="send_confirmation_email"
                checked={formData.send_confirmation_email}
                onChange={handleChange}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>📧 Send Confirmation Email</span>
            </label>
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', display: 'block', marginLeft: '26px' }}>
              Send an email to authenticated users after they submit a response
            </small>

            {formData.send_confirmation_email && (
              <div style={{ marginTop: '1rem', marginLeft: '26px', padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                    Email Subject
                  </label>
                  <input
                    type="text"
                    name="confirmation_email_subject"
                    value={formData.confirmation_email_subject}
                    onChange={handleChange}
                    placeholder="Thank you for completing {{survey_title}}"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      fontFamily: 'inherit'
                    }}
                  />
                  <small style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                    Use {'{{survey_title}}'} for survey name, {'{{response_id}}'} for response ID
                  </small>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                    Email Template (HTML)
                  </label>
                  <textarea
                    name="confirmation_email_template"
                    value={formData.confirmation_email_template}
                    onChange={handleChange}
                    placeholder="<h2>Thank you!</h2><p>Your response to {{survey_title}} has been received.</p>"
                    rows={6}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      fontFamily: 'monospace',
                      resize: 'vertical'
                    }}
                  />
                  <small style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                    HTML template with {'{{variables}}'}. Available: survey_title, response_id, submission_date
                  </small>
                </div>
              </div>
            )}
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              cursor: 'pointer',
              marginBottom: '0.75rem'
            }}>
              <input
                type="checkbox"
                name="webhook_enabled"
                checked={formData.webhook_enabled}
                onChange={handleChange}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>Enable Webhook (Integrations)</span>
            </label>
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', display: 'block', marginLeft: '26px' }}>
              Call a webhook URL when survey is completed (for external integrations, automations, etc.)
            </small>

            {formData.webhook_enabled && (
              <div style={{ marginTop: '1rem', marginLeft: '26px', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '2px solid #86efac' }}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                    Webhook URL
                  </label>
                  <input
                    type="url"
                    name="webhook_url"
                    value={formData.webhook_url}
                    onChange={handleChange}
                    placeholder="https://your-server.com/api/gift-card-webhook"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #86efac',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      fontFamily: 'monospace'
                    }}
                  />
                  <small style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginTop: '0.5rem', display: 'block' }}>
                    💡 <strong>Webhook Payload:</strong> Receives survey completion data including survey_id, response_id, user_id, and answers
                  </small>

                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                background: 'var(--bg-secondary)',
                color: 'var(--text-secondary)',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                background: loading ? 'var(--bg-secondary)' : 'var(--button-bg)',
                color: 'var(--button-text)',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              {loading ? 'Creating...' : 'Create Survey'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateSurvey;
